import { Progress as ProgressPrimitive } from "radix-ui"

import { theme, type ThemeKeys } from "../../theme"
import { Box } from "../Box"
import { Text } from "../Text"
import styles from "./SegmentedProgressBar.module.css"

export interface SegmentedProgressBarProps {
  /**
   * The current value of the progress.
   */
  value: number
  /**
   * The maximum value of the progress.
   *
   * @defaultValue 100
   */
  max?: number
  /**
   * The threshold value above which the progress bar will be colored differently.
   * @defaultValue `max`
   */
  threshold?: number
  /**
   * Value label is always attached to the element for the sake of
   * screen-readers.
   * If getValueLabel is not provided, the attached
   * label will be % value of progress.
   */
  getValueLabel?: (value: number, max: number) => string
  /**
   * Label for the progress segment below the threshold.
   */
  belowThresholdLabel?: string

  /**
   * Label for the progress segment above the threshold.
   */
  aboveThresholdLabel?: string
}

export function SegmentedProgressBar(props: SegmentedProgressBarProps) {
  const {
    value,
    max = 100,
    threshold = max,
    getValueLabel,
    belowThresholdLabel,
    aboveThresholdLabel,
  } = props

  const boundedValue = Math.min(Math.max(value, 0), max)

  const thresholdPercentage = (threshold / max) * 100
  const valuePercentage = (boundedValue / max) * 100

  const inlinePadding = theme.space[6]
  const insetRadius = theme.space[8]

  const insetValue =
    valuePercentage >= 100 ? inlinePadding : `calc(100% - ${valuePercentage}%)`

  function thresholdGradient(
    color1: ThemeKeys<"color">,
    color2: ThemeKeys<"color">,
  ) {
    return `linear-gradient(to right, ${theme.color[color1]} ${thresholdPercentage}%, ${theme.color[color2]} ${thresholdPercentage}%)`
  }

  return (
    <div>
      <ProgressPrimitive.Root
        className={styles.progressBar}
        style={{
          background: thresholdGradient("green4", "amber4"),
        }}
        value={boundedValue}
        max={max}
        getValueLabel={getValueLabel}
      >
        <ProgressPrimitive.Indicator
          className={styles.progressBarIndicator}
          style={{
            background: thresholdGradient("green8", "amber10"),
            clipPath: `inset(0 ${insetValue} 0 ${inlinePadding} round ${insetRadius})`,
          }}
        />
      </ProgressPrimitive.Root>
      {belowThresholdLabel || aboveThresholdLabel ? (
        <Box margin={{ top: 16 }}>
          <Box width={`${thresholdPercentage}%`} justifyContent="center">
            {belowThresholdLabel && (
              <Text variant="small" color="grey11">
                {belowThresholdLabel}
              </Text>
            )}
          </Box>
          <Box justifyContent="center" flexGrow={1}>
            {aboveThresholdLabel && threshold < max && (
              <Text variant="small" color="grey11">
                {aboveThresholdLabel}
              </Text>
            )}
          </Box>
        </Box>
      ) : undefined}
    </div>
  )
}
